import React, { useEffect, useRef, useState } from 'react';
import { Textarea } from 'baseui/textarea';
import { Button } from 'baseui/button';
import { useStyletron } from 'baseui';

type TwoWayChatInputBoxProps = {
  inputValue: string;
  submitButtonText?: string;
  onChange: (value: string) => void;
  onSubmit: (value: string) => void;
  onTyping: () => void;
  maxLength?: number;
  variant?: 'primary' | 'secondary';
  placeholder?: string;
  translation?: {
    originalMessage: string;
    rawTranslation: string;
    translatedMessage: string;
    edited: boolean;
  } | null;
  setTranslation?: (
    value: {
      originalMessage: string;
      rawTranslation: string;
      translatedMessage: string;
      edited: boolean;
    } | null,
  ) => void;
  setTranslateInputValue?: (value: string) => void;
  focusTranslateInput?: () => void;
  textAreaRef: React.RefObject<HTMLTextAreaElement>;
  dataTestid?: string;
};

export const TwoWayChatInputBox = ({
  inputValue,
  submitButtonText = 'Submit',
  onChange,
  onSubmit,
  onTyping,
  maxLength = 2000,
  variant = 'primary',
  placeholder = '',
  setTranslateInputValue,
  translation,
  setTranslation,
  focusTranslateInput,
  textAreaRef,
  dataTestid = 'two-way-chat-input',
}: TwoWayChatInputBoxProps) => {
  const [css] = useStyletron();
  const [isFocused, setIsFocused] = useState(false);
  const prevInputLengthRef = useRef(0);
  const baseBgColor = variant === 'primary' ? '#3693BF' : '#37474F';
  const bgColor = isFocused ? baseBgColor : `${baseBgColor}80`;
  const onKeyPress = (e) => {
    if (inputValue.length < maxLength) {
      if (e.keyCode === 13 || (e.charCode === 13 && !e.shiftKey)) {
        e.preventDefault();
        if (submitButtonText === 'Send' && !inputValue.trim()) {
          return;
        }
        onSubmit(inputValue);
        if (submitButtonText === 'Send') {
          setTranslateInputValue && setTranslateInputValue('');
          setTranslation && setTranslation(null);
          textAreaRef.current && (textAreaRef.current.value = '');
          onChange('');
          focusTranslateInput && focusTranslateInput();
        }
      } else if (inputValue.length) {
        onTyping();
      }
    }
  };
  const handleSubmit = () => {
    if (submitButtonText === 'Send' && !inputValue.trim()) {
      return;
    }
    onSubmit(inputValue);
    if (submitButtonText === 'Send') {
      setTranslateInputValue && setTranslateInputValue('');
      setTranslation && setTranslation(null);
      onChange('');
      focusTranslateInput && focusTranslateInput();
    }
  };
  const resizeTextarea = () => {
    const el = textAreaRef.current;
    if (!el) return;

    const lineHeight = parseInt(getComputedStyle(el).lineHeight) || 24;
    const maxRows = 4;
    const maxHeight = lineHeight * maxRows;

    // Only set height:'auto' when content got shorter (deletion or clear).
    // height:'auto' forces a synchronous layout that briefly collapses the
    // textarea, causing the messages container's scrollTop to be clamped and
    // producing a visible jump. When growing, scrollHeight already reflects
    // the content height correctly without the reset.
    if (inputValue.length < prevInputLengthRef.current) {
      el.style.height = 'auto';
    }
    prevInputLengthRef.current = inputValue.length;

    el.style.height = Math.min(el.scrollHeight, maxHeight) + 'px';
    el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden';
  };

  useEffect(() => {
    resizeTextarea();
  }, [inputValue]);

  return (
    <div
      className={css({
        display: 'flex',
        marginTop: '2px',
        flexDirection: 'row',
        padding: '8px',
        backgroundColor: bgColor,
        width: '100%',
        borderRadius: '10px',
        transition: 'background-color 0.2s ease',
        dir: 'auto',
      })}
    >
      <Textarea
        onChange={(e: any) => {
          if (setTranslation && translation) {
            setTranslation({
              originalMessage: translation.originalMessage,
              rawTranslation: translation.rawTranslation,
              translatedMessage: e.target.value,
              edited: true,
            });
          }
          onChange(e.target.value);
        }}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        value={inputValue}
        inputRef={textAreaRef}
        onKeyPress={onKeyPress}
        maxLength={maxLength}
        placeholder={placeholder}
        id={dataTestid}
        rows={2}
        overrides={{
          Input: {
            props: {
              dir: 'auto',
              'data-testid': dataTestid,
            },
          },
        }}
      />
      <Button
        overrides={{
          BaseButton: {
            style: () => ({
              width: '100px',
              flexShrink: 0,
              backgroundColor: bgColor,
            }),
          },
        }}
        onClick={() => {
          handleSubmit();
        }}
        disabled={!inputValue.trim() || inputValue.length === maxLength}
        data-testid={`${dataTestid}-action`}
      >
        {submitButtonText}
      </Button>
    </div>
  );
};
